import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import { useHistory } from "react-router-dom";
import { Button, Checkbox, Loader, Modal } from "semantic-ui-react";
import styled from "styled-components";
import { t } from "../../../constant/translations";
import { reverse, routes } from "../../../routes";
import { device } from "../../../config";
import { fetchSurveys, closeSurvey } from "../../../redux/modules/survey";
import { RoleRequired } from "../../auth/role-required";
import { permissions } from "../../../constant/permissions";

const ListWrap = styled.div`
  margin-top: 28px;
`;

const SectionLabel = styled.div`
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #a8a29e;
  margin-bottom: 8px;
`;

const TableWrap = styled.div`
  overflow-x: auto;
`;

const SurveyTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;

  th {
    text-align: left;
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.04em;
    color: #a8a29e;
    padding: 6px 10px;
    border-bottom: 1px solid #e7e5e4;
    white-space: nowrap;
  }

  td {
    padding: 8px 10px;
    border-bottom: 1px solid #f0f0ef;
    vertical-align: middle;
  }

  tr:last-child td {
    border-bottom: none;
  }

  @media ${device.desktop} {
    th,
    td {
      padding: 8px 12px;
    }
  }
`;

const HeaderRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
`;

const StatusBadge = styled.span`
  display: inline-block;
  font-size: 10px;
  font-weight: 600;
  border-radius: 10px;
  padding: 2px 8px;
  white-space: nowrap;
  ${({ status }) => {
    if (status === "active") return "background: #dbeafe; color: #1d4ed8;";
    if (status === "closed") return "background: #e7e5e4; color: #78716c;";
    return "background: #fef3c7; color: #92400e;";
  }}
`;

const ParticipationWrap = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 90px;
`;

const BarOuter = styled.div`
  flex: 1;
  height: 6px;
  background: #e9ecef;
  border-radius: 3px;
  overflow: hidden;
  min-width: 50px;
`;

const BarInner = styled.div`
  height: 100%;
  background: ${({ active }) => (active ? "#1a7fc1" : "#e9ecef")};
  width: ${({ pct }) => pct}%;
  border-radius: 3px;
`;

const BarLabel = styled.span`
  font-size: 11px;
  color: #777;
  white-space: nowrap;
`;

const TitleCell = styled.td`
  font-weight: 500;
  color: #1c1917;
`;

const MutedCell = styled.td`
  color: #78716c;
  white-space: nowrap;
`;

const ActionCell = styled.td`
  text-align: right;
  white-space: nowrap;
`;

const InfoBox = styled.div`
  margin-top: 12px;
  padding: 10px 14px;
  background: #f8f9fb;
  border: 1px solid #e7e5e4;
  border-radius: 4px;
  font-size: 11px;
  color: #78716c;
  line-height: 1.5;
`;

const EmptyMessage = styled.div`
  padding: 20px 0;
  font-size: 13px;
  color: #a8a29e;
  text-align: center;
`;

function formatDate(dt) {
  if (!dt) return "–";
  return new Date(dt).toLocaleDateString("bg-BG");
}

function SurveyListComponent({
  block,
  isManager,
  surveys,
  loading,
  fetchSurveys,
  closeSurvey,
}) {
  const history = useHistory();
  const [closingId, setClosingId] = useState(null);
  const [sendNotifications, setSendNotifications] = useState(true);
  const [closing, setClosing] = useState(false);

  useEffect(() => {
    if (block) fetchSurveys(block.id);
  }, [block && block.id]);

  const goTo = (path) => history.push(path);

  const handleConfirmClose = () => {
    setClosing(true);
    closeSurvey(closingId, { skipNotifications: !sendNotifications })
      .then(() => fetchSurveys(block.id))
      .finally(() => {
        setClosing(false);
        setClosingId(null);
        setSendNotifications(true);
      });
  };

  if (loading) return <Loader active inline="centered" size="small" />;

  const blockSurveys = (surveys || []).filter((s) => s.block === block.id);

  if (blockSurveys.length === 0 && !isManager) return null;

  return (
    <ListWrap>
      <HeaderRow>
        <SectionLabel>{t("survey.title")}</SectionLabel>
        {isManager && (
          <Button
            size="tiny"
            primary
            onClick={() =>
              goTo(reverse(routes.blockSurveyNew, { blockId: block.id }))
            }
          >
            <i className="plus icon" />
            {t("survey.new")}
          </Button>
        )}
      </HeaderRow>

      {blockSurveys.length === 0 ? (
        <EmptyMessage>{t("survey.empty")}</EmptyMessage>
      ) : (
        <TableWrap>
          <SurveyTable>
            <thead>
              <tr>
                <th>{t("survey.topic")}</th>
                <th>{t("survey.questions")}</th>
                <th>{t("survey.created_at")}</th>
                <th>{t("survey.end_date")}</th>
                <th>{t("survey.participation")}</th>
                <th></th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {blockSurveys.map((survey) => {
                const pct = survey.voted_weight_pct
                  ? parseFloat(survey.voted_weight_pct)
                  : 0;
                const questions = survey.questions || [];
                return (
                  <tr key={survey.id}>
                    <TitleCell>{survey.title}</TitleCell>
                    <td
                      style={{
                        color: "#1a7fc1",
                        fontWeight: 600,
                        textAlign: "center",
                      }}
                    >
                      {questions.length}
                    </td>
                    <MutedCell>{formatDate(survey.created_at)}</MutedCell>
                    <MutedCell>{formatDate(survey.end_date)}</MutedCell>
                    <td>
                      <ParticipationWrap>
                        <BarOuter>
                          <BarInner
                            pct={pct}
                            active={survey.status !== "draft"}
                          />
                        </BarOuter>
                        <BarLabel>
                          {survey.vote_count || 0} {t("survey.estates_count")}
                        </BarLabel>
                      </ParticipationWrap>
                    </td>
                    <td>
                      <StatusBadge status={survey.status}>
                        {t(`survey.status_${survey.status}`)}
                      </StatusBadge>
                    </td>
                    <ActionCell>
                      {survey.status === "active" && (
                        <RoleRequired permission={permissions.surveyEdit}>
                          <Button
                            size="mini"
                            basic
                            onClick={() =>
                              goTo(
                                reverse(routes.blockSurveyResults, {
                                  blockId: block.id,
                                  surveyId: survey.id,
                                })
                              )
                            }
                          >
                            {t("survey.results")}
                          </Button>
                          <Button
                            size="mini"
                            basic
                            negative
                            onClick={() => setClosingId(survey.id)}
                          >
                            {t("survey.close_poll")}
                          </Button>
                        </RoleRequired>
                      )}
                      {survey.status === "active" && (
                        <RoleRequired userOnly={true}>
                          <Button
                            size="mini"
                            primary
                            onClick={() =>
                              goTo(
                                reverse(routes.blockSurveyVote, {
                                  blockId: block.id,
                                  surveyId: survey.id,
                                })
                              )
                            }
                          >
                            {t("survey.cast_vote")}
                          </Button>
                        </RoleRequired>
                      )}
                      {survey.status === "closed" && (
                        <Button
                          size="mini"
                          basic
                          onClick={() =>
                            goTo(
                              reverse(routes.blockSurveyResults, {
                                blockId: block.id,
                                surveyId: survey.id,
                              })
                            )
                          }
                        >
                          {t("survey.results")}
                        </Button>
                      )}
                      {survey.status === "draft" && (
                        <RoleRequired permission={permissions.surveyEdit}>
                          <Button
                            size="mini"
                            basic
                            onClick={() =>
                              goTo(
                                reverse(routes.blockSurveyEdit, {
                                  blockId: block.id,
                                  surveyId: survey.id,
                                })
                              )
                            }
                          >
                            {t("labels.change")}
                          </Button>
                        </RoleRequired>
                      )}
                    </ActionCell>
                  </tr>
                );
              })}
            </tbody>
          </SurveyTable>
        </TableWrap>
      )}

      {blockSurveys.length > 0 && (
        <InfoBox>{t("survey.not_anonymous")}</InfoBox>
      )}

      <Modal
        open={closingId !== null}
        onClose={() => !closing && setClosingId(null)}
        size="tiny"
      >
        <Modal.Header>{t("survey.close_poll")}</Modal.Header>
        <Modal.Content>
          <p>{t("survey.close_confirm")}</p>
          <Checkbox
            toggle
            label={t("survey.send_notifications")}
            checked={sendNotifications}
            onChange={(_, { checked }) => setSendNotifications(checked)}
          />
        </Modal.Content>
        <Modal.Actions>
          <Button basic onClick={() => setClosingId(null)} disabled={closing}>
            {t("common.back")}
          </Button>
          <Button
            negative
            loading={closing}
            disabled={closing}
            onClick={handleConfirmClose}
          >
            {t("survey.close_poll")}
          </Button>
        </Modal.Actions>
      </Modal>
    </ListWrap>
  );
}

const mapStateToProps = (state) => ({
  isManager: state.auth.user?.is_manager,
  surveys: state.survey.entities,
  loading: state.survey.loading,
});

export const SurveyList = connect(mapStateToProps, {
  fetchSurveys,
  closeSurvey,
})(SurveyListComponent);
